import React, { useState, useEffect } from 'react';import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, 
  ChevronUp, 
  CheckCircle, 
  Circle, 
  BookOpen, 
  PenLine, 
  Brain, 
  Users, 
  FileText,
  AlertTriangle,
  Clock,
  Apple
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Task status indicators
const TaskStatus = {
  COMPLETED: 'completed',
  PENDING: 'pending',
  REQUIRED: 'required',
  OPTIONAL: 'optional',
  URGENT: 'urgent'
};

// Single Task Item
function TaskItem({ 
  icon: Icon, 
  title, 
  status = TaskStatus.PENDING, 
  onClick, 
  description,
  progress,
  isUrgent = false,
  isHighlight = false,
  testId
}) {
  const { t } = useLanguage();
  const isCompleted = status === TaskStatus.COMPLETED;
  
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200
        ${isUrgent 
          ? 'bg-red-50 border-2 border-red-300 hover:bg-red-100' 
          : isCompleted 
            ? 'bg-green-50 border border-green-200 hover:bg-green-100' 
            : isHighlight
              ? 'bg-amber-50 border-2 border-amber-300 hover:bg-amber-100'
              : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-blue-300'
        }
      `}
      data-testid={testId}
    >
      {/* Status Icon */}
      <div className={`
        p-2 rounded-full
        ${isUrgent 
          ? 'bg-red-100 text-red-600' 
          : isCompleted 
            ? 'bg-green-100 text-green-600' 
            : isHighlight
              ? 'bg-amber-200 text-amber-700'
              : 'bg-blue-100 text-blue-600'
        }
      `}>
        {isCompleted ? (
          <CheckCircle className="w-5 h-5" />
        ) : isUrgent ? (
          <AlertTriangle className="w-5 h-5" />
        ) : (
          <Icon className="w-5 h-5" />
        )}
      </div>

      {/* Task Info */}
      <div className="flex-1 text-left">
        <div className="flex items-center gap-2">
          <span className={`font-medium text-sm ${isCompleted ? 'text-green-700' : isUrgent ? 'text-red-700' : 'text-gray-800'}`}>
            {title}
          </span>
          {isUrgent && (
            <span className="bg-white border-2 border-black text-black text-sm font-semibold px-2 py-0.5 rounded-full">
              {t('notifications.urgent')}
            </span>
          )}
          {status === TaskStatus.REQUIRED && !isCompleted && (
            <span className="bg-white border-2 border-black text-black text-sm font-semibold px-2 py-0.5 rounded-full">
              {t('tasks.required')}
            </span>
          )}
        </div>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
        {progress !== undefined && (
          <div className="mt-2">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${isCompleted ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">{progress}% complete</p>
          </div>
        )}
      </div>

      {/* Arrow */}
      <ChevronDown className={`w-4 h-4 ${isUrgent ? 'text-red-400' : 'text-gray-400'} rotate-[-90deg]`} />
    </button>
  );
}

// Task Dropdown Section
export function TaskDropdown({ 
  title, 
  subtitle,
  tasks = [], 
  defaultOpen = false,
  urgentCount = 0,
  completedCount = 0,
  totalCount = 0,
  // When `collapseWhenComplete` is true the dropdown auto-closes the moment
  // every task is done.  Used on Daily Tasks so a green-tick day cleans up
  // the dashboard surface.  The user can still click the header to reopen.
  collapseWhenComplete = false,
  footer = null,
}) {
  const isAllDone = completedCount === totalCount && totalCount > 0;
  const initialOpen = collapseWhenComplete && isAllDone ? false : defaultOpen;
  const [isOpen, setIsOpen] = useState(initialOpen);
  // Auto-collapse the moment every task ticks green (props update after the
  // initial mount once /api/progress resolves).  We only force the close;
  // re-opening is left to the user.
  React.useEffect(() => {
    if (collapseWhenComplete && isAllDone) {
      setIsOpen(false);
    }
  }, [collapseWhenComplete, isAllDone]);  const { t } = useLanguage();
  const navigate = useNavigate();

  const hasUrgent = urgentCount > 0;
  const allCompleted = isAllDone;

  return (
    <div className="rounded-xl overflow-hidden border-2 border-black bg-white">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white text-black"
        data-testid={`task-dropdown-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <span className="font-bold text-base">{title}</span>
          
          {/* Must Complete badge */}
          {subtitle && !allCompleted && (
            <span className="text-sm font-semibold px-2 py-0.5 rounded-full bg-white border-2 border-black text-black">
              {subtitle}
            </span>
          )}
          
          {/* Counters */}
          <div className="flex items-center gap-2">
            {hasUrgent && (
              <span className="bg-white border-2 border-black text-black text-sm font-bold px-2 py-0.5 rounded-full">
                {urgentCount} {t('notifications.urgent')}
              </span>
            )}
            <span className="text-sm px-2 py-0.5 rounded-full bg-white border-2 border-black text-black">
              {completedCount}/{totalCount}
            </span>
          </div>
        </div>

        {isOpen ? (
          <ChevronUp className="w-5 h-5" />
        ) : (
          <ChevronDown className="w-5 h-5" />
        )}
      </button>

      {/* Tasks List */}
      {isOpen && (
        <div className="p-3 space-y-2 bg-gray-50">
          {tasks.length === 0 ? (
            <p className="text-center text-gray-500 py-4 text-sm">No tasks</p>
          ) : (
            tasks.map((task, index) => (
              <TaskItem
                key={task.id || index}
                icon={task.icon || Circle}
                title={task.title}
                status={task.status}
                description={task.description}
                progress={task.progress}
                isUrgent={task.isUrgent}
                isHighlight={task.isHighlight}
                onClick={() => task.route && navigate(task.route)}
                testId={task.testId}
              />
            ))
          )}
          {footer}
        </div>
      )}
    </div>
  );
}

// Daily Tasks Component
export function DailyTasksDropdown({ 
  journalCompleted = false,
  mentalHealthCompleted = false,
  personalHistoryCompleted = false,
  personalHistoryProgress = 0,
  personalHistoryMissingSections = [],
  isInTwoWeekWindow = true,
  notifications = []
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Build tasks array - Personal History at TOP during 2 week window until completed
  const tasks = [];

  // Personal History - only show during 2 week window and if not completed
  if (isInTwoWeekWindow && !personalHistoryCompleted) {
    tasks.push({
      id: 'personal-history',
      icon: FileText,
      title: t('tasks.personalHistory'),
      status: personalHistoryProgress === 100 ? TaskStatus.COMPLETED : TaskStatus.REQUIRED,
      description: 'Must be completed before Week 1 begins',
      progress: personalHistoryProgress,
      route: '/personal-history',
      testId: 'task-personal-history'
    });
  }

  // Urgent notifications (drug screens, etc.)
  notifications
    .filter(n => n.type === 'urgent' || n.type === 'drugScreen')
    .forEach((notification, idx) => {
      tasks.push({
        id: `notification-${idx}`,
        icon: AlertTriangle,
        title: notification.title,
        status: TaskStatus.URGENT,
        description: notification.message,
        isUrgent: true,
        route: notification.route || '/notifications',
        testId: `task-notification-urgent-${idx}`
      });
    });

  // Mental Health check-ins moved to Weekly Tasks (3x/wk in 2WW, 2x/wk thereafter).
  // The daily-task list no longer surfaces this.

  // Journal
  tasks.push({
    id: 'journal',
    icon: PenLine,
    title: t('tasks.journal'),
    status: journalCompleted ? TaskStatus.COMPLETED : TaskStatus.REQUIRED,
    description: journalCompleted ? 'Completed today' : 'Required daily',
    route: '/journal',
    testId: 'task-journal'
  });

  // Profile completion reminder during 2-week window — surface specific missing sections
  if (isInTwoWeekWindow && !personalHistoryCompleted) {
    const missing = (personalHistoryMissingSections || []);
    const description = missing.length > 0
      ? `${personalHistoryProgress}% complete: still to fill: ${missing.slice(0, 4).join(', ')}${missing.length > 4 ? `, +${missing.length - 4} more` : ''}`
      : `Your Personal History is ${personalHistoryProgress}% complete: finish it to unlock Week 1`;
    tasks.push({
      id: 'profile-reminder',
      icon: FileText,
      title: missing.length > 0 ? 'Complete Your Profile' : 'Review Your Profile',
      status: TaskStatus.PENDING,
      description,
      route: '/personal-history',
      testId: 'task-profile-reminder',
      isHighlight: true
    });
  }

  // Non-urgent notifications (exclude meeting reminders - those go to Weekly Tasks)
  notifications
    .filter(n => n.type !== 'urgent' && n.type !== 'drugScreen' && n.type !== 'meetingReminder' && n.type !== 'meeting')
    .forEach((notification, idx) => {
      tasks.push({
        id: `notification-info-${idx}`,
        icon: Clock,
        title: notification.title,
        status: TaskStatus.PENDING,
        description: notification.message,
        route: notification.route || '/notifications',
        testId: `task-notification-${idx}`
      });
    });

  const completedCount = tasks.filter(t => t.status === TaskStatus.COMPLETED).length;
  const urgentCount = tasks.filter(t => t.isUrgent).length;

  return (
    <TaskDropdown
      title={t('dashboard.dailyTasks')}
      subtitle="Must Complete"
      tasks={tasks}
      defaultOpen={true}
      collapseWhenComplete={true}
      urgentCount={urgentCount}
      completedCount={completedCount}
      totalCount={tasks.length}
    />
  );
}

// Weekly Tasks Component

// Small SVG progress ring used on the "2 Week Window Tasks" tile so users can
// see progress filling as each sub-item (Introduction, Personal History,
// Admission Assessment, H.O.W. Commitment, Stickman) is completed — without
// having to open the /self-work page.
function ProgressRing({ progress = 0, testId }) {
  const clamped = Math.max(0, Math.min(100, progress));
  const size = 20;
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circumference;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="flex-shrink-0"
      data-testid={testId}
      aria-label={`${Math.round(clamped)}% complete`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#e5e7eb"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#2563eb"
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={circumference / 4}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

export function WeeklyTasksDropdown({ 
  meetingsLogged = 0,
  meetingsRequired = 5,
  readingsCompleted = false,
  selfWorkCompleted = false,
  selfWorkProgress = 0,
  selfWorkCompletedCount = 0,
  selfWorkTotal = 5,
  currentWeek = 1,
  meetingNotifications = [],
  dietPrefsCompleted = false,
  weeklyMentalHealth = 0,
  weeklyMentalHealthRequired = 2,
  // Self-Work reminder pulled in from the dashboard so the bespoke reminder
  // card lives INSIDE Weekly Tasks instead of cluttering the dashboard surface.
  // Shape: { level: 'info' | 'warn', text: string } or null.
  swReminder = null,
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showMeetingNotifs, setShowMeetingNotifs] = useState(false);

  const meetingNotifCount = meetingNotifications.filter(n => 
    n.type === 'meetingReminder' || n.type === 'meeting'
  ).length;

  const isInTwoWeekWindow = currentWeek <= 0;

  const tasks = [
    {
      id: 'meetings',
      icon: Users,
      title: t('tasks.meetingLog'),
      status: meetingsLogged >= meetingsRequired ? TaskStatus.COMPLETED : TaskStatus.REQUIRED,
      description: `${meetingsLogged}/${meetingsRequired} meetings logged`,
      progress: Math.min(100, (meetingsLogged / meetingsRequired) * 100),
      route: '/meetings',
      testId: 'task-meetings',
      hasNotifications: meetingNotifCount > 0,
      notificationCount: meetingNotifCount
    },
    {
      id: 'mental-health',
      icon: Brain,
      title: t('tasks.mentalHealthCheck'),
      status: weeklyMentalHealth >= weeklyMentalHealthRequired ? TaskStatus.COMPLETED : TaskStatus.REQUIRED,
      description: `${weeklyMentalHealth}/${weeklyMentalHealthRequired} this week  ·  ${weeklyMentalHealthRequired === 2 ? 'Twice per week (random days)' : 'Once per week (random day)'}`,
      progress: Math.min(100, (weeklyMentalHealth / weeklyMentalHealthRequired) * 100),
      route: '/mental-health',
      testId: 'task-weekly-mental-health'
    },
    {
      id: 'readings',
      icon: BookOpen,
      title: isInTwoWeekWindow ? '2 Week Window Readings' : `Programme Week ${currentWeek} Reading`,
      status: readingsCompleted ? TaskStatus.COMPLETED : TaskStatus.REQUIRED,
      description: isInTwoWeekWindow
        ? 'Complete 2 Week Window readings in the Content Library'
        : `Complete Programme Week ${currentWeek} reading`,
      route: '/library',
      testId: 'task-readings'
    },
    {
      id: 'self-work',
      icon: PenLine,
      title: isInTwoWeekWindow ? '2 Week Window Tasks' : `Programme Week ${currentWeek} Self-Work`,
      status: selfWorkCompleted ? TaskStatus.COMPLETED : TaskStatus.REQUIRED,
      description: isInTwoWeekWindow
        ? (selfWorkCompletedCount > 0
            ? `${selfWorkCompletedCount}/${selfWorkTotal} complete · Introduction, assessments & foundational self-work`
            : 'Introduction, assessments & foundational self-work')
        : `Complete Programme Week ${currentWeek} lesson and self-work`,
      progress: selfWorkProgress,
      showRing: isInTwoWeekWindow, // render circular progress ring during 2WW
      route: '/self-work',
      testId: 'task-self-work'
    },
    {
      id: 'diet-recovery',
      icon: Apple,
      title: 'Diet & Recovery',
      status: dietPrefsCompleted ? TaskStatus.COMPLETED : (isInTwoWeekWindow ? TaskStatus.REQUIRED : TaskStatus.PENDING),
      description: dietPrefsCompleted
        ? 'Preferences saved: Diet Library is filtered to your needs'
        : (isInTwoWeekWindow
          ? 'Complete your dietary preferences during the 2-week window'
          : 'Review and update your dietary preferences'),
      route: '/diet-recovery',
      testId: 'task-diet-recovery'
    }
  ];

  const completedCount = tasks.filter(t => t.status === TaskStatus.COMPLETED).length;

  // Custom render for Weekly Tasks with meeting notifications dropdown
  const [isOpen, setIsOpen] = useState(false);
  const title = t('dashboard.weeklyTasks');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid="weekly-tasks-dropdown"
      >
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-gray-900">{title}</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            {completedCount}/{tasks.length}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {/* Tasks */}
      {isOpen && (
        <div className="p-3 space-y-2 bg-gray-50">
          {tasks.map((task, index) => (
            <div key={task.id}>
              {/* Task Item */}
              <button
                onClick={() => {
                  if (task.id === 'meetings' && task.hasNotifications) {
                    setShowMeetingNotifs(!showMeetingNotifs);
                  } else {
                    task.route && navigate(task.route);
                  }
                }}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200
                  ${task.status === TaskStatus.COMPLETED 
                    ? 'bg-green-50 border border-green-200 hover:bg-green-100' 
                    : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                  }
                `}
                data-testid={task.testId}
              >
                <div className={`
                  p-2 rounded-full
                  ${task.status === TaskStatus.COMPLETED 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-blue-100 text-blue-600'
                  }
                `}>
                  <task.icon className="w-4 h-4" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${
                      task.status === TaskStatus.COMPLETED ? 'text-green-700' : 'text-gray-900'
                    }`}>
                      {task.title}
                    </span>
                    {task.hasNotifications && (
                      <span className="bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                        {task.notificationCount}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{task.description}</span>
                </div>
                {task.status === TaskStatus.COMPLETED ? (
                  <CheckCircle className="w-5 h-5 text-green-500" data-testid={`${task.testId}-check`} />
                ) : task.showRing ? (
                  <ProgressRing progress={task.progress || 0} testId={`${task.testId}-ring`} />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300" />
                )}
              </button>

              {/* Meeting Notifications Sub-dropdown */}
              {task.id === 'meetings' && showMeetingNotifs && meetingNotifications.length > 0 && (
                <div className="ml-6 mt-2 space-y-1 border-l-2 border-black pl-3">
                  <p className="text-base font-semibold text-black mb-2">
                    Meeting reminders
                  </p>
                  {meetingNotifications
                    .filter(n => n.type === 'meetingReminder' || n.type === 'meeting')
                    .map((notif, idx) => (
                      <button
                        key={idx}
                        onClick={() => navigate(notif.route || '/meetings/zoom')}
                        className="w-full flex items-center gap-2 p-2 bg-white border-2 border-black rounded-md text-left hover:bg-zinc-50 transition-colors"
                        data-testid={`meeting-notif-${idx}`}
                      >
                        <Clock className="w-4 h-4 text-black" />
                        <div className="flex-1">
                          <p className="text-base font-medium text-black">{notif.title}</p>
                          <p className="text-base text-black">{notif.message}</p>
                        </div>
                      </button>
                    ))
                  }
                  <button
                    onClick={() => navigate('/meetings')}
                    className="w-full text-base text-black hover:underline py-1"
                  >
                    Go to Meeting Log →
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Self-Work reminder  --  the same call-to-action that used to
              live as a separate card on the dashboard.  Same look, same
              click behaviour;  just nested inside Weekly Tasks so the
              dashboard reads cleanly. */}
          {swReminder && (
            <button
              onClick={() => navigate('/library?tab=self-work')}
              className="w-full mt-1 rounded-lg px-3 py-2.5 flex items-center gap-3 text-left border-2 border-black bg-white hover:bg-zinc-50 text-black"
              data-testid="sw-reminder-card"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-white border-2 border-black text-black">
                <BookOpen className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-black">
                  Self-Work
                </div>
                <div className="text-base leading-tight text-black">{swReminder.text}</div>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TaskDropdown;
